import { makeAutoObservable, runInAction } from 'mobx';

import { earningsService } from '@/services/earnings';
import { usersService } from '@/services/users';
import type { Earning, MeStats } from '@/types/earning';

const PER_PAGE = 50;

class EarningsStore {
  balanceCents = 0;
  lifetimeEarnedCents = 0;
  monthEarningsCents = 0;

  earnings: Earning[] = [];
  page = 0;
  hasMore = false;

  refreshing = false;
  refreshError: string | null = null;

  loadingMore = false;
  loadingMoreError: string | null = null;

  loaded = false;

  // Tracks the in-flight loadMore promise so concurrent callers await the
  // actual page load rather than getting an immediately-resolved `undefined`.
  // Internal — readers should observe `loadingMore` instead. Not `private`
  // because MobX's AnnotationsMap can only annotate public keys.
  inflightLoadMore: Promise<void> | null = null;

  constructor() {
    makeAutoObservable(this, { inflightLoadMore: false }, { autoBind: true });
  }

  /** True before the first successful refresh — drives the initial spinner. */
  get isInitialLoading(): boolean {
    return this.refreshing && !this.loaded;
  }

  /**
   * Idempotent loader for tab mount effects. Skips when a refresh is
   * already in flight or when data has already arrived. Components
   * call this in their mount effect; pull-to-refresh should call
   * `refresh()` directly to force a re-fetch.
   */
  ensureLoaded(): Promise<void> {
    if (this.refreshing) return Promise.resolve();
    if (this.loaded) return Promise.resolve();
    return this.refresh().catch(() => {
      // Error captured on store.refreshError; callers don't need to handle.
    });
  }

  /**
   * Pulls fresh stats and the first page of earnings in parallel.
   * Resets the existing list to the new first page (no append). Concurrent
   * refresh calls: last-write-wins, mirrors DealsStore.load semantics.
   */
  async refresh(): Promise<void> {
    runInAction(() => {
      this.refreshing = true;
      this.refreshError = null;
    });
    try {
      const [stats, response] = await Promise.all([
        usersService.stats(),
        earningsService.list({ page: 1, perPage: PER_PAGE }),
      ]);
      runInAction(() => {
        this.applyStats(stats);
        this.earnings = response.earnings;
        this.page = response.meta.page;
        this.hasMore = response.meta.has_more;
        this.loaded = true;
      });
    } catch (err) {
      runInAction(() => {
        this.refreshError = err instanceof Error ? err.message : 'Failed to load earnings.';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.refreshing = false;
      });
    }
  }

  /**
   * Fetches the next page and appends to the list. Concurrent callers
   * receive the in-flight promise so their `await` resolves when the
   * page actually lands (not immediately on the short-circuit). Does
   * NOT touch stats — those only change on full refresh. On error the
   * page counter does not advance, so a retry hits the same page.
   */
  loadMore(): Promise<void> {
    if (this.inflightLoadMore) return this.inflightLoadMore;
    if (!this.hasMore) return Promise.resolve();
    if (!this.loaded) return Promise.resolve();

    const promise = this.runLoadMore();
    this.inflightLoadMore = promise;
    return promise;
  }

  private async runLoadMore(): Promise<void> {
    const nextPage = this.page + 1;
    runInAction(() => {
      this.loadingMore = true;
      this.loadingMoreError = null;
    });
    try {
      const response = await earningsService.list({ page: nextPage, perPage: PER_PAGE });
      runInAction(() => {
        this.earnings = [...this.earnings, ...response.earnings];
        this.page = response.meta.page;
        this.hasMore = response.meta.has_more;
      });
    } catch (err) {
      runInAction(() => {
        this.loadingMoreError = err instanceof Error ? err.message : 'Failed to load more.';
      });
      throw err;
    } finally {
      runInAction(() => {
        this.loadingMore = false;
      });
      this.inflightLoadMore = null;
    }
  }

  reset(): void {
    runInAction(() => {
      this.balanceCents = 0;
      this.lifetimeEarnedCents = 0;
      this.monthEarningsCents = 0;
      this.earnings = [];
      this.page = 0;
      this.hasMore = false;
      this.refreshing = false;
      this.refreshError = null;
      this.loadingMore = false;
      this.loadingMoreError = null;
      this.loaded = false;
    });
  }

  private applyStats(stats: MeStats): void {
    this.balanceCents = stats.balance_cents;
    this.lifetimeEarnedCents = stats.lifetime_earned_cents;
    this.monthEarningsCents = stats.month_earnings_cents;
  }
}

export const earningsStore = new EarningsStore();
// `EarningsStore` class is exported for tests so each test gets a fresh
// instance. Production code should use the `earningsStore` singleton.
export { EarningsStore };
